import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  EyeIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

interface BackgroundCheck {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  checkType: 'personality' | 'social_media' | 'criminal' | 'employment' | 'education';
  status: 'pending' | 'completed' | 'failed' | 'in_progress';
  results: {
    score: number;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    findings: string[];
    aiAnalysis: string;
    flags: string[];
  };
  socialMediaAccounts: {
    platform: string;
    username: string;
    analysisScore: number;
    flags: string[];
  }[];
  requestedAt: Date;
  completedAt?: Date;
  reviewedBy?: string;
  notes?: string;
}

interface BackgroundCheckSummary {
  pending: number;
  completed: number;
  failed: number;
  highRisk: number;
  totalChecks: number;
  averageProcessingTime: number;
}

const BackgroundChecks: React.FC = () => {
  const { adminUser, hasPermission } = useAdmin();
  const [backgroundChecks, setBackgroundChecks] = useState<BackgroundCheck[]>([]);
  const [summary, setSummary] = useState<BackgroundCheckSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedRisk, setSelectedRisk] = useState<string>('all');

  useEffect(() => {
    fetchBackgroundChecks();
  }, [selectedType, selectedStatus, selectedRisk]);

  const fetchBackgroundChecks = async () => {
    try {
      setLoading(true);
      
      const response = await adminApiService.getBackgroundChecks(
        1, 50,
        selectedType !== 'all' ? selectedType : undefined,
        selectedStatus !== 'all' ? selectedStatus : undefined,
        selectedRisk !== 'all' ? selectedRisk : undefined
      );
      
      if (response.success && response.data) {
        // Transform backend data to frontend format
        const backendChecks = response.data.backgroundChecks || [];
        const transformedChecks: BackgroundCheck[] = backendChecks.map((check: any) => ({
          id: check.id,
          userId: check.userId,
          userName: check.userName || 'Unknown User',
          userEmail: check.userEmail || 'unknown@example.com',
          checkType: check.checkType || 'social_media',
          status: check.status || 'pending',
          results: {
            score: check.results?.score || 0,
            riskLevel: check.results?.riskLevel || 'low',
            findings: check.results?.findings || [],
            aiAnalysis: check.results?.aiAnalysis || 'No analysis available',
            flags: check.results?.flags || []
          },
          socialMediaAccounts: check.socialMediaAccounts || [],
          requestedAt: check.requestedAt ? new Date(check.requestedAt.seconds * 1000) : new Date(),
          completedAt: check.completedAt ? new Date(check.completedAt.seconds * 1000) : undefined,
          reviewedBy: check.reviewedBy,
          notes: check.notes
        }));

        const summary: BackgroundCheckSummary = {
          pending: response.data.summary?.pending || 0,
          completed: response.data.summary?.completed || 0,
          failed: response.data.summary?.failed || 0,
          highRisk: response.data.summary?.highRisk || 0,
          totalChecks: response.data.summary?.totalChecks || 0,
          averageProcessingTime: response.data.summary?.averageProcessingTime || 0
        };

        setBackgroundChecks(transformedChecks);
        setSummary(summary);
        
        // Show a message if the feature isn't fully implemented
        if (response.message && transformedChecks.length === 0) {
          toast(response.message);
        }
      } else {
        throw new Error('Failed to load background check data');
      }
    } catch (error: any) {
      console.error('Failed to fetch background checks:', error);
      toast.error('Failed to load background check data');
    } finally {
      setLoading(false);
    }
  };

  const handleReprocessCheck = async (checkId: string) => {
    try {
      // This would call a real API endpoint
      toast.success('Background check reprocessing initiated');
      fetchBackgroundChecks(); // Refresh data
    } catch (error: any) {
      toast.error('Failed to reprocess background check');
    }
  };

  const getTypeIcon = (type: string) => {
    const icons = {
      personality: UserIcon,
      social_media: GlobeAltIcon,
      criminal: ShieldCheckIcon,
      employment: DocumentTextIcon,
      education: DocumentTextIcon
    };
    const Icon = icons[type as keyof typeof icons] || ShieldCheckIcon;
    return <Icon className="h-5 w-5" />;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, label: 'Pending' },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, label: 'Completed' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircleIcon, label: 'Failed' },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: ClockIcon, label: 'In Progress' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getRiskBadge = (riskLevel: string) => {
    const riskColors = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${riskColors[riskLevel as keyof typeof riskColors]}`}>
        {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} Risk
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Background Checks</h1>
          <p className="mt-2 text-sm text-gray-700">
            AI-powered background screening and social media analysis
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Run Batch Check
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-yellow-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Pending</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.pending}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-6 w-6 text-green-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.completed}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <XCircleIcon className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Failed</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.failed}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-6 w-6 text-orange-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">High Risk</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.highRisk}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ShieldCheckIcon className="h-6 w-6 text-blue-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Checks</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.totalChecks}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ClockIcon className="h-6 w-6 text-purple-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Avg Time</dt>
                    <dd className="text-lg font-medium text-gray-900">{summary.averageProcessingTime}d</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                Check Type:
              </label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Types</option>
                <option value="personality">Personality Analysis</option>
                <option value="social_media">Social Media</option>
                <option value="criminal">Criminal Background</option>
                <option value="employment">Employment History</option>
                <option value="education">Education Verification</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                Status:
              </label>
              <select
                id="status"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div>
              <label htmlFor="risk" className="block text-sm font-medium text-gray-700">
                Risk Level:
              </label>
              <select
                id="risk"
                value={selectedRisk}
                onChange={(e) => setSelectedRisk(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              >
                <option value="all">All Risk Levels</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Background Checks List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {backgroundChecks.map((check) => (
            <li key={check.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 flex-1">
                    <div className="flex-shrink-0 mr-4">
                      {getTypeIcon(check.checkType)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {check.userName}
                        </p>
                        {getStatusBadge(check.status)}
                        {check.status === 'completed' && getRiskBadge(check.results.riskLevel)}
                      </div>
                      
                      <div className="text-sm text-gray-500 mb-2">
                        <span className="font-medium">Email:</span> {check.userEmail} • 
                        <span className="font-medium"> Type:</span> {check.checkType.replace('_', ' ')} • 
                        <span className="font-medium"> Requested:</span> {new Date(check.requestedAt).toLocaleDateString()}
                        {check.completedAt && (
                          <>
                            {' • '}
                            <span className="font-medium">Completed:</span> {new Date(check.completedAt).toLocaleDateString()}
                          </>
                        )}
                      </div>
                      
                      {check.status === 'completed' && (
                        <>
                          <div className="text-sm text-gray-600 mb-2">
                            <span className="font-medium">Score:</span> {check.results.score}/100 • 
                            <span className="font-medium"> AI Analysis:</span> {check.results.aiAnalysis}
                          </div>
                          
                          {check.results.findings.length > 0 && (
                            <div className="mb-2">
                              <span className="text-sm font-medium text-gray-700">Key Findings:</span>
                              <ul className="text-sm text-gray-600 list-disc list-inside">
                                {check.results.findings.slice(0, 2).map((finding, index) => (
                                  <li key={index}>{finding}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {check.results.flags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {check.results.flags.map((flag) => (
                                <span
                                  key={flag}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                                >
                                  {flag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {check.socialMediaAccounts.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {check.socialMediaAccounts.map((account) => (
                                <span
                                  key={account.platform}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {account.platform}: {account.analysisScore}/100
                                </span>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="ml-2 flex-shrink-0 flex space-x-2">
                    {check.status === 'failed' && (
                      <button
                        onClick={() => handleReprocessCheck(check.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Reprocess
                      </button>
                    )}
                    <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                      <EyeIcon className="h-4 w-4 mr-1" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {backgroundChecks.length === 0 && (
        <div className="text-center py-12">
          <ShieldCheckIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No background checks found</h3>
          <p className="mt-1 text-sm text-gray-500">
            No background checks match the current filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default BackgroundChecks;